import { computed, ref } from 'vue'
import { createInjectionState, toReactive } from '@vueuse/core'
import {
  ToQueryStringFilters,
  defaultStatuses,
  recordActions,
  useToQueryString,
} from '@/core'
import type { TReception, TReceptionDetail } from '../../..'
import { TRecordAction } from '@/core/types'
import { useRoute, useRouter } from 'vue-router'
import { useReceptionDetailsQuery } from '../query/useReceptionDetailsQuery'
import { useModuleQuery } from '../query/useModuleQuery'
import { useOCDetailsQuery } from '../query/useOCDetailsQuery'

const [useProvideReceptionModule, useReceptionModule] = createInjectionState(
  () => {
    const ocSelected = ref<number | string>()
    const form = ref<TReception>({})
    const detail = ref<TReceptionDetail>({})
    const purchaseOrdersFilters = ref<ToQueryStringFilters>({
      param: '',
      status_id: [13],
      id: [],
      order_by: 'created_at,desc',
    })
    const locationsFilters = ref<ToQueryStringFilters>({
      warehouse_id: [],
      status_id: [1],
    })

    const route = useRoute()
    const router = useRouter()
    const isEdit = computed(() => !!route.params?.id?.toString())
    const isEditReception = computed(() => route.name === 'receptionsDetails')
    const isEditCoils = computed(() => route.name === 'receptionsReceipts')
    const ocQueryParams = useToQueryString(toReactive(purchaseOrdersFilters), {
      searchColumns: ['id', 'supplier.name'],
    })

    const locationsQueryParams = useToQueryString(toReactive(locationsFilters))

    const receptionsQuery = useModuleQuery({
      modelId: computed(() => route.params?.id?.toString()),
      enableQuery: {
        loadRecords: false,
        loadRecord: computed(() => !!route.params?.id),
        loadHistoryActions: false,
      },
      handlers: {
        onLoadRecord: (reception) => {
          if (
            isApplied.value &&
            reception?.status?.name === defaultStatuses.APPLIED
          ) {
            router.replace({
              name: 'receptionsReceipts',
              params: { id: reception.id },
            })
          }

          if (reception?.purchase_order?.id) {
            purchaseOrdersFilters.value.id = [reception.purchase_order?.id]
          }

          form.value = { ...reception }
        },
      },
    })

    const ocDetailsQuery = useOCDetailsQuery({
      modelId: computed(() => ocSelected.value?.toString() ?? ''),
      enableQuery: {
        loadRecords: computed(() => !!ocSelected.value && !isEditCoils.value),
      },
    })

    const receptionDetailQuery = useReceptionDetailsQuery({
      modelId: computed(() => route.params?.id?.toString()),
      enableQuery: {
        loadRecords: computed(
          () =>
            !receptionsQuery.loadRecord.isLoading.value && isEditCoils.value,
        ),
        purchaseOrdersCatalog: computed(() => {
          if (isEditReception.value) {
            return (
              !!receptionsQuery.loadRecord.isSuccess.value &&
              !!purchaseOrdersFilters.value?.id?.[0]
            )
          }
          return true
        }),
        locationsCatalog: computed(
          () => !!(locationsQueryParams?.params.value.length > 1),
        ),
        warehouseCatalog: computed(() => !isEditCoils.value),
      },
      filters: {
        purchaseOrdersCatalog: ocQueryParams.queryParams,
        locationsCatalog: locationsQueryParams.queryParams,
      },
    })

    const onAdd = async (payload: TReception) => {
      const reception = await receptionsQuery.addRecord.mutateAsync(payload)
      redirectTo(reception)
    }

    const onEdit = async (payload: TReception) => {
      await receptionsQuery.editRecord.mutateAsync(payload)
      await receptionsQuery.loadRecord.refetch()
    }

    const onAction = async (payload: TRecordAction) => {
      await receptionsQuery.actionRecord.mutateAsync(payload)
      await receptionDetailQuery.loadRecords.refetch()
    }

    const redirectTo = (payload: TReception) => {
      router.replace({
        name: 'receptions',
        query: {
          reception: payload.id,
          action: 'create',
          redirect: 'receptionsDetails',
        },
      })
    }

    const onLoadDetails = async () => {
      await receptionDetailQuery.loadRecords.refetch()
    }

    const isApplied = computed(
      () => route.query.action === recordActions.applied,
    )
    const model = computed(() => receptionsQuery.loadRecord?.data?.value)
    const purchaseOrdersCatalog = computed(
      () => receptionDetailQuery.purchaseOrdersCatalog.data.value,
    )
    const warehouseCatalog = computed(
      () => receptionDetailQuery.warehouseCatalog.data.value,
    )
    const isOCCatalogLoading = computed(
      () => receptionDetailQuery.purchaseOrdersCatalog.isLoading.value,
    )
    const isWarehouseCatalogLoading = computed(
      () => receptionDetailQuery.warehouseCatalog.isLoading.value,
    )
    const isDetailsLoading = computed(() =>
      isEditReception.value
        ? receptionDetailQuery.loadRecords.isLoading.value
        : ocDetailsQuery.loadRecords.isLoading.value && !!ocSelected.value,
    )

    const details = computed(() => ocDetailsQuery.loadRecords?.data?.value)

    const detailsCoil = computed(
      () => receptionDetailQuery.loadRecords?.data?.value,
    )

    const locations = computed(
      () => receptionDetailQuery.locationsCatalog?.data?.value,
    )

    const isLoadingLocations = computed(
      () => receptionDetailQuery.locationsCatalog.isLoading.value,
    )
    const isLoading = computed(() => {
      if (isEditReception.value || isEditCoils.value) {
        return (
          receptionsQuery.addRecord.isLoading.value ||
          receptionsQuery.editRecord.isLoading.value ||
          receptionsQuery.loadRecord.isLoading.value ||
          receptionsQuery.loadRecord.isFetching.value
        )
      }

      return isWarehouseCatalogLoading.value
    })

    return {
      ocSelected,
      model,
      purchaseOrdersFilters,
      form,
      isEditReception,
      isEditCoils,
      isLoading,
      details,
      isDetailsLoading,
      purchaseOrdersCatalog,
      warehouseCatalog,
      locations,
      isLoadingLocations,
      locationsFilters,
      isOCCatalogLoading,
      isApplied,
      detail,
      detailsCoil,
      isEdit,
      onLoadDetails,
      onAdd,
      onEdit,
      onAction,
    }
  },
)

export { useProvideReceptionModule, useReceptionModule }
